import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Facebook, Instagram, Mail } from 'lucide-react';
import { toast } from 'sonner';
import BUSINESS_INFO from '../config/businessInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim().toLowerCase(), source: 'footer' });
      setSubscribed(true);
      setEmail('');
      toast.success('Welcome to the Club! Check your inbox for 10% off.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.info(detail || "You're already subscribed!");
        setSubscribed(true);
      } else {
        toast.error(detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const social = BUSINESS_INFO.social || {};
  const hasAnySocial = social.instagram || social.facebook || social.tiktok;

  return (
    <footer className="bg-white border-t border-[#E0D5D0] mt-12 md:mt-20">
      <div className="max-w-4xl mx-auto px-5 md:px-6 lg:px-8 py-12 md:py-16">

        {/* Brand */}
        <div className="text-center mb-10">
          <h3 className="font-playfair text-2xl font-bold text-perfect-peach mb-3">
            The Perfect Peach Club
          </h3>
          <p className="text-african-earth text-sm leading-relaxed max-w-md mx-auto mb-5">
            Balance, not correction. Gentle care when things feel off, and support when things feel right.
          </p>
          {(hasAnySocial || true) && (
            <div className="flex gap-3 justify-center">
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-bone-white flex items-center justify-center text-african-earth hover:bg-perfect-peach hover:text-white transition-all duration-300" data-testid="instagram-link" aria-label="Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {social.tiktok && (
                <a href={social.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-bone-white flex items-center justify-center text-african-earth hover:bg-perfect-peach hover:text-white transition-all duration-300" data-testid="tiktok-link" aria-label="TikTok">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
                </a>
              )}
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-bone-white flex items-center justify-center text-african-earth hover:bg-perfect-peach hover:text-white transition-all duration-300" data-testid="facebook-link" aria-label="Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              <a href={`mailto:${BUSINESS_INFO.email}`} className="w-10 h-10 rounded-full bg-bone-white flex items-center justify-center text-african-earth hover:bg-perfect-peach hover:text-white transition-all duration-300" data-testid="email-link" aria-label="Email">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          )}
        </div>

        {/* Newsletter Signup */}
        <div className="max-w-md mx-auto mb-10 bg-bone-white rounded-2xl px-5 py-6 sm:px-7 sm:py-7 text-center">
          <h4 className="font-playfair text-lg font-semibold text-[#2D2420] mb-1.5">
            Join the Club
          </h4>
          <p className="text-african-earth text-sm mb-4 leading-relaxed">
            Get <strong>10% off your first order</strong> over R500, plus early access to drops and rituals.
          </p>
          {subscribed ? (
            <p className="text-perfect-peach text-sm font-medium" data-testid="newsletter-success">
              You're in. Check your inbox for your code.
            </p>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                className="flex-1 px-4 py-2.5 rounded-full border border-[#E0D5D0] bg-white text-sm text-[#2D2420] placeholder:text-african-earth/50 focus:outline-none focus:ring-2 focus:ring-perfect-peach focus:border-transparent transition-all"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 rounded-full bg-perfect-peach text-white text-sm font-medium hover:bg-perfect-peach/90 transition-colors disabled:opacity-60 whitespace-nowrap"
                data-testid="newsletter-submit-btn"
              >
                {submitting ? 'Joining…' : 'Join'}
              </button>
            </form>
          )}
        </div>

        {/* Links — two groups side by side */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-3 gap-x-6 max-w-2xl mx-auto text-center mb-10">
          <Link to="/products" className="text-african-earth hover:text-perfect-peach transition-colors text-sm" data-testid="footer-products-link">Shop</Link>
          <Link to="/about" className="text-african-earth hover:text-perfect-peach transition-colors text-sm">Our Story</Link>
          <Link to="/track-order" className="text-african-earth hover:text-perfect-peach transition-colors text-sm" data-testid="footer-track-order-link">Track Order</Link>
          <Link to="/order-history" className="text-african-earth hover:text-perfect-peach transition-colors text-sm" data-testid="footer-order-history-link">Order History</Link>
          <Link to="/wishlist" className="text-african-earth hover:text-perfect-peach transition-colors text-sm" data-testid="footer-wishlist-link">Wishlist</Link>
          <Link to="/referral" className="text-african-earth hover:text-perfect-peach transition-colors text-sm" data-testid="footer-referral-link">Refer a Friend</Link>
          <Link to="/shipping" className="text-african-earth hover:text-perfect-peach transition-colors text-sm">Shipping</Link>
          <Link to="/returns" className="text-african-earth hover:text-perfect-peach transition-colors text-sm">Returns</Link>
        </div>

        {/* Email */}
        <div className="text-center mb-8">
          <a href={`mailto:${BUSINESS_INFO.email}`} className="text-african-earth hover:text-perfect-peach transition-colors text-sm">
            {BUSINESS_INFO.email}
          </a>
        </div>

        {/* Copyright */}
        <div className="border-t border-[#E0D5D0] pt-6 text-center">
          <p className="text-xs text-african-earth/60">
            &copy; {new Date().getFullYear()} The Perfect Peach Club. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
